/**
 * reward-whitelist-terms-html — dựng đoạn HTML "danh sách shop/sản phẩm áp dụng"
 * từ whitelist của đợt, để admin chèn vào ô Điều khoản.
 *
 * Vì sao cần dù đã có block riêng ở app: block "áp dụng cho" phải chờ phát hành
 * app mới hiện trên máy user cũ, còn Điều khoản là HTML nên hiện được NGAY trên
 * cả app cũ lẫn web. Chèn xong admin sửa chữ tự do — đây là văn bản, không phải
 * dữ liệu ràng buộc; sửa ở đây KHÔNG đổi luật xét thưởng.
 *
 * ⚠️ TUYỆT ĐỐI không đặt link sàn trần làm nút mua: Điều khoản là HTML TĨNH dùng
 * chung mọi user nên không thể nhúng sub_id riêng ai. Ai bấm link trần cũng mua
 * "trắng" → mất hoàn tiền VÀ trượt thưởng của đợt. "Mua ngay" trỏ về endpoint
 * `/reward-program/:slug/go` — backend gắn mã theo user lúc render/lúc bấm.
 *
 * Chỉ dùng thẻ Quill hiểu (<p>, <strong>, <em>, <br>, <img>, <a>) — thẻ lạ
 * (table, div) bị Quill nuốt khi admin bấm lưu, dán vào là mất chữ.
 */

const CAMP_LABELS = { shopee: "Shopee", lazada: "Lazada", tiktok: "TikTok" };

/**
 * Link mua đi qua DOMAIN CHÍNH, không phải api.longhousee.com — dù cả hai cùng về
 * một backend (nginx longhousee.com có sẵn `location /api/v1/` proxy sang :8080).
 *
 * Lý do không dùng `api.`:
 *   1. Chỉ longhousee.com nằm trong App Links / Universal Links ⇒ sau này app bắt
 *      được link này để mở native. Link `api.` thì vĩnh viễn phải qua trình duyệt.
 *   2. Link nằm trong nội dung user đọc — phơi đường dẫn API nội bộ vừa xấu vừa
 *      buộc HTML cũ phải sửa nếu đổi domain API.
 *
 * Phải là URL TUYỆT ĐỐI: app nạp HTML này từ domain khác, đường dẫn tương đối sẽ 404.
 */
const SITE_ORIGIN = process.env.REACT_APP_FRONTEND_URL || "https://longhousee.com";
const API_ORIGIN = `${SITE_ORIGIN}/api/v1`;

/**
 * Link "Mua ngay" — trỏ về `/di-den`, KHÔNG gắn với đợt nào.
 *
 * Tách khỏi `:slug` có chủ đích: `/di-den` chỉ cần `url` + `uid` nên dùng lại được
 * cho bài viết, kênh cộng đồng, banner — không riêng đợt thưởng.
 *
 * Lúc backend render điều khoản, nó xử lý link này theo sàn:
 *   Shopee        → thay thẳng bằng an_redir đã gắn mã (ghép chuỗi, khỏi thêm chặng)
 *   Lazada/TikTok → nối `&uid=<userId>&rp=<id đợt>` (phải gọi API sàn nên để tới lúc bấm)
 * Nhờ `uid` nằm sẵn trong link, `/di-den` KHÔNG cần phiên đăng nhập ⇒ mở trong trình
 * duyệt của app vẫn gắn đúng mã, hết cảnh bắt đăng nhập lại.
 *
 * ⚠️ ĐỔI DẠNG Ở ĐÂY LÀ BACKEND KHÔNG DÒ RA NỮA (rewrite-terms-buy-links.js) —
 * user sẽ bấm nhằm link chưa gắn mã.
 */
function buyUrl(entry) {
    return `${API_ORIGIN}/di-den?url=${encodeURIComponent(entry.url)}`;
}

/** Cạnh ảnh thu nhỏ (px) — đủ nhận ra sản phẩm mà không chiếm hết màn hình. */
const THUMB_SIZE = 96;

/** Chống vỡ HTML khi tên sản phẩm có &, <, " (tên sàn hay có "S&K", 'size <M>'). */
function escapeHtml(v) {
    return String(v == null ? "" : v)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/**
 * Một "thẻ" sản phẩm: ảnh (nếu có) → tên đậm → sàn + 2 nút.
 * Dùng <p> xếp chồng vì Quill không có table; trên app/web đọc vẫn rõ ràng.
 */
function renderEntry(e) {
    const label = escapeHtml(e.name || e.productId || e.shopId || "");
    if (!label) return "";
    const camp = escapeHtml(CAMP_LABELS[e.campId] || e.campId || "");
    const parts = [];

    // width/height BẮT BUỘC: không có thì ảnh ăn trọn chiều ngang, một sản phẩm
    // chiếm gần hết màn — danh sách 5 SP phải cuộn mãi. Quill 2 giữ nguyên 2 thuộc
    // tính này khi lưu (Image blot cho phép alt/width/height).
    if (e.image) {
        parts.push(`<p><img src="${escapeHtml(e.image)}" width="${THUMB_SIZE}" height="${THUMB_SIZE}"></p>`);
    }

    // Backend xử lý link này lúc render điều khoản (rewrite-terms-buy-links.js).
    const tail = e.url ? ` — <a href="${escapeHtml(buyUrl(e))}">Mua ngay</a>` : "";
    parts.push(`<p><strong>${label}</strong><br><em>${camp}</em>${tail}</p>`);
    return parts.join("");
}

function renderSection(title, entries) {
    const items = entries.map(renderEntry).filter(Boolean).join("");
    if (!items) return "";
    return `<p><strong>${escapeHtml(title)}</strong></p>${items}`;
}

/**
 * @param {object} config config đợt đang điền trên form
 * @returns {string} HTML rỗng khi đợt không giới hạn shop/sản phẩm
 */
export function buildWhitelistTermsHtml(config = {}) {
    const shops = Array.isArray(config.shopWhitelist) ? config.shopWhitelist : [];
    const products = Array.isArray(config.productWhitelist) ? config.productWhitelist : [];
    const sections = [
        renderSection("Sản phẩm được thưởng", products),
        renderSection("Shop được thưởng", shops),
    ].filter(Boolean);
    if (!sections.length) return "";
    return (
        sections.join("") +
        "<p>Chỉ đơn có ít nhất 1 shop/sản phẩm trong danh sách trên mới được tính thưởng. " +
        "Bấm <strong>Mua ngay</strong> ở danh sách trên để mở link đã gắn mã của bạn — " +
        "tự tìm sản phẩm trên sàn rồi mua thì đơn không được ghi nhận hoàn tiền.</p>"
    );
}

export default buildWhitelistTermsHtml;
